import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Settings, Info, Shield, Share2, Check, ExternalLink,
  Apple, Activity, Watch, Bot, Brain, LogOut
} from 'lucide-react';
import { useProfileStore } from '../store/profileStore';
import { useNavigate } from 'react-router-dom';
import { UserProfileDialog } from './UserProfileDialog';

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
  notificationCount: number;
}

interface Integration {
  id: string;
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  lastSync?: string;
  type: 'app' | 'device';
}

const integrations: Integration[] = [
  { 
    id: 'apple-health',
    name: 'Apple Health',
    icon: <Apple className="w-5 h-5" />,
    connected: true,
    lastSync: '2 mins ago',
    type: 'app'
  },
  {
    id: 'apple-watch',
    name: 'Apple Watch',
    icon: <Watch className="w-5 h-5" />,
    connected: true,
    lastSync: '1 min ago',
    type: 'device'
  },
  {
    id: 'myfitnesspal',
    name: 'MyFitnessPal',
    icon: <Activity className="w-5 h-5" />,
    connected: true,
    lastSync: '5 mins ago',
    type: 'app'
  }
];

export function SideMenu({ isOpen, onClose, notificationCount }: SideMenuProps) {
  const { resetProfile, isNerdMode, toggleNerdMode } = useProfileStore();
  const navigate = useNavigate();
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleReset = () => {
    resetProfile();
    navigate('/', { replace: true });
    onClose();
  };

  const connectedApps = integrations.filter(i => i.connected && i.type === 'app');
  const connectedDevices = integrations.filter(i => i.connected && i.type === 'device');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
          />

          {/* Side Menu */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed top-0 left-0 bottom-0 w-80 bg-background/95 backdrop-blur-xl border-r border-border z-50 flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-border bg-background/95 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-medium">Guest User</div>
                    <button
                      onClick={() => setShowProfileDialog(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      View Profile Settings
                    </button>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto theme-scrollbar">
              <div className="p-4 space-y-6">
                {/* Connected Apps & Devices */}
                {(connectedApps.length > 0 || connectedDevices.length > 0) && (
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground px-2">Connected</div>
                    <div className="space-y-1">
                      {connectedApps.map((app) => (
                        <div
                          key={app.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                        >
                          <div className="flex items-center gap-3">
                            {app.icon}
                            <div>
                              <div className="text-sm">{app.name}</div>
                              <div className="text-xs text-muted-foreground">{app.lastSync}</div>
                            </div>
                          </div>
                          <Check className="w-4 h-4 text-success" />
                        </div>
                      ))}
                      {connectedDevices.map((device) => (
                        <div
                          key={device.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                        >
                          <div className="flex items-center gap-3">
                            {device.icon}
                            <div>
                              <div className="text-sm">{device.name}</div>
                              <div className="text-xs text-muted-foreground">{device.lastSync}</div>
                            </div>
                          </div>
                          <Check className="w-4 h-4 text-success" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* App Settings */}
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground px-2">App Settings</div>
                  <div className="space-y-1">
                    <button
                      onClick={() => setShowProfileDialog(true)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={() => toggleNerdMode()}
                      className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                        isNerdMode ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Brain className="w-5 h-5" />
                        <span>Fitness Nerd Mode</span>
                      </div>
                      <div className={`w-11 h-6 rounded-full transition-colors relative ${
                        isNerdMode ? 'bg-primary' : 'bg-muted-foreground/20'
                      }`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          isNerdMode ? 'right-1' : 'left-1'
                        }`} />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Help & Support */}
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground px-2">Help & Support</div>
                  <div className="space-y-1">
                    <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <Info className="w-5 h-5" />
                      <span>Help Center</span>
                    </button>
                    <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <Shield className="w-5 h-5" />
                      <span>Privacy Policy</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-background/95 backdrop-blur-xl">
              <button
                onClick={handleReset}
                className="w-full flex items-center gap-3 p-2 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Reset Profile</span>
              </button>
            </div>
          </motion.div>

          {/* Profile Dialog */}
          {showProfileDialog && (
            <UserProfileDialog onClose={() => setShowProfileDialog(false)} />
          )}
        </>
      )}
    </AnimatePresence>
  );
}